/**
 * Parsing e formattazione date sessione: sempre interpretazione giorno/mese/anno
 * per stringhe con separatori (evita MM/DD del motore JS).
 */

const IT_DATE_TIME: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/** Massima durata ragionevole per una sessione (7 giorni) in minuti. */
const MAX_SESSION_DURATION_MINUTES = 7 * 24 * 60;

const DD_MM_YYYY_TIME =
  /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[,\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;

/** ISO senza offset/Z: tipico da MySQL DATETIME dopo toISOString() — va letto come UTC. */
const NAIVE_ISO_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(\.\d+)?$/;

/**
 * Interpreta stringhe tipo "12/02/2026, 16:29" come giorno/mese/anno (Europeo).
 * ISO 8601 con Z/offset resta invariato. ISO "naive" (senza timezone) è trattato come UTC
 * (valori persistiti da toISOString() e restituiti da MySQL senza Z).
 */
export function parseSessionTimestampToIso(raw: string): string | null {
  const s = raw.trim();
  if (!s) {
    return null;
  }

  const m = s.match(DD_MM_YYYY_TIME);
  if (m) {
    const day = Number.parseInt(m[1], 10);
    const month = Number.parseInt(m[2], 10);
    const year = Number.parseInt(m[3], 10);
    const hh = m[4] !== undefined ? Number.parseInt(m[4], 10) : 0;
    const min = m[5] !== undefined ? Number.parseInt(m[5], 10) : 0;
    const sec = m[6] !== undefined ? Number.parseInt(m[6], 10) : 0;

    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hh > 23 ||
      min > 59 ||
      sec > 59
    ) {
      return null;
    }

    const d = new Date(year, month - 1, day, hh, min, sec, 0);
    if (
      d.getFullYear() !== year ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null;
    }
    return d.toISOString();
  }

  const naive = s.match(NAIVE_ISO_DATE_TIME);
  if (naive) {
    const asUtc = `${naive[1]}-${naive[2]}-${naive[3]}T${naive[4]}:${naive[5]}:${naive[6]}${naive[7] ?? ''}Z`;
    const parsedNaive = Date.parse(asUtc);
    if (!Number.isNaN(parsedNaive)) {
      return new Date(parsedNaive).toISOString();
    }
  }

  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString();
    }
  }

  return null;
}

/**
 * Normalizza il timestamp di fine sessione per il salvataggio (sempre ISO UTC).
 * Preferisce parsing europeo per evitare MM/DD su stringhe "dd/mm/yyyy".
 */
export function normalizeSessionEndedAtForPersist(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return new Date().toISOString();
  }
  const fromEuropean = parseSessionTimestampToIso(trimmed);
  if (fromEuropean) {
    return fromEuropean;
  }
  const fallback = Date.parse(trimmed);
  if (!Number.isNaN(fallback)) {
    return new Date(fallback).toISOString();
  }
  return new Date().toISOString();
}

/**
 * Mostra data/ora in italiano (giorno/mese/anno). Accetta ISO o stringhe dd/mm/yyyy.
 */
export function formatItalianSessionDateTime(
  value: string | null | undefined
): string {
  if (value == null || String(value).trim() === '') {
    return '—';
  }
  const s = String(value).trim();
  const iso = parseSessionTimestampToIso(s);
  const d = iso ? new Date(iso) : new Date(s);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleString('it-IT', IT_DATE_TIME);
}

/**
 * Durata in minuti tra due timestamp; "—" se invalidi, negativi o fuori range.
 */
export function formatSessionDurationBetween(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start?.trim() || !end?.trim()) {
    return '0 min';
  }
  const startIso = parseSessionTimestampToIso(start);
  const endIso = parseSessionTimestampToIso(end);
  const startMs = startIso ? new Date(startIso).getTime() : NaN;
  const endMs = endIso ? new Date(endIso).getTime() : NaN;
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return '—';
  }
  const diffMinutes = Math.round((endMs - startMs) / 60_000);
  if (diffMinutes < 0 || diffMinutes > MAX_SESSION_DURATION_MINUTES) {
    return '—';
  }
  return `${diffMinutes} min`;
}

/**
 * Durata dettagliata (min + s) tra due timestamp sessione.
 * Usa lo stesso parser di formatItalianSessionDateTime (naive ISO = UTC).
 */
export function formatSessionDurationDetailed(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start?.trim() || !end?.trim()) {
    return '0 s';
  }
  const startIso = parseSessionTimestampToIso(start);
  const endIso = parseSessionTimestampToIso(end);
  if (!startIso || !endIso) {
    return '—';
  }
  const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(diffMs)) {
    return '—';
  }
  if (diffMs < 0 || diffMs > MAX_SESSION_DURATION_MINUTES * 60_000) {
    return '—';
  }
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes} min ${seconds} s`;
  }
  return `${seconds} s`;
}
